import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { EMAIL_REG_EX } from '../../utils/regex';

import './UserSearchDropdown.scss';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 3;

const UserSearchDropdown = ({
  value,
  onChange,
  organisationId = null,
  placeholder = 'Search by name or email…',
  adminSearch = false,
  allowEmailFallback = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);
  // Set right before an in-progress edit clears the selection (see
  // handleInputChange) so the sync effect below doesn't stomp the keystroke
  // that triggered it — Reset/Clear/reopening the panel don't set this, so
  // those still clear the visible text as expected
  const skipNextSyncRef = useRef(false);

  // Reflect external value changes into the input — a fallback email-only
  // value (no matching user account) has no firstName/surname, so fall back
  // to showing the email itself
  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    if (value) {
      setInputValue(
        value.firstName ? `${value.firstName} ${value.surname}` : value.email
      );
    } else {
      setInputValue('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(
    async (q) => {
      setLoading(true);
      try {
        const endpoint = adminSearch ? '/users/search' : '/org/users/search';
        const params = { q };
        if (adminSearch && organisationId) {
          params.organisationId = organisationId;
        }
        const resp = await axios.get(endpoint, { params });
        setResults(resp.data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [adminSearch, organisationId]
  );

  const handleInputChange = (e) => {
    const q = e.target.value;
    setInputValue(q);

    // If the user edits after a selection, clear the selection — flagged so
    // the sync effect doesn't overwrite the text just typed above
    if (value) {
      skipNextSyncRef.current = true;
      onChange(null);
    }

    clearTimeout(debounceTimer.current);

    if (q.trim().length < MIN_QUERY_LEN) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceTimer.current = setTimeout(() => search(q), DEBOUNCE_MS);
  };

  const handleSelect = (user) => {
    onChange(user);
    setInputValue(`${user.firstName} ${user.surname}`);
    setResults([]);
    setOpen(false);
  };

  // Accepts a typed email with no matching user account as the filter value
  // — only offered when allowEmailFallback is set and the search came back empty
  const handleSelectEmail = (email) => {
    onChange({ email });
    setInputValue(email);
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setInputValue('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="usd-wrapper" ref={wrapperRef}>
      <div className="usd-input-row">
        <input
          className="usd-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && <span className="usd-spinner" />}
        {value && !loading && (
          <button className="usd-clear" onClick={handleClear} type="button">
            ×
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <ul className="usd-results">
          {results.map((user) => (
            <li
              key={user.id}
              className="usd-result-item"
              onMouseDown={() => handleSelect(user)}
            >
              <span className="usd-result-name">
                {user.firstName} {user.surname}
              </span>
              <span className="usd-result-email">{user.email}</span>
            </li>
          ))}
        </ul>
      )}
      {open &&
        !loading &&
        results.length === 0 &&
        (allowEmailFallback && EMAIL_REG_EX.test(inputValue.trim()) ? (
          <ul className="usd-results">
            <li
              className="usd-result-item"
              onMouseDown={() => handleSelectEmail(inputValue.trim())}
            >
              <span className="usd-result-name">
                {t('users.search.use_input', { value: inputValue.trim() })}
              </span>
              <span className="usd-result-email">
                {t('users.search.no_matching_user_filter_by_this_email')}
              </span>
            </li>
          </ul>
        ) : (
          <div className="usd-no-results">{t('common.no_users_found')}</div>
        ))}
    </div>
  );
};

export default UserSearchDropdown;
